import React, { useState, useEffect } from 'react';
import PropertyChart from './_propertychart';
import { useGetLandlordPropertyQuery } from '../../../app/lib/apiSlice/property/propertySlice.tsx';
import { useGetPropertyReportMetricsQuery } from '../../../app/lib/apiSlice/reportsSlice/reportSlice.tsx';

export default function PropertyMetricsGraphical() {
    const [hoveredMetric, setHoveredMetric] = useState(null);
    const [selectedApartment, setSelectedApartment] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [dateFilterType, setDateFilterType] = useState('');
    const [showDateModal, setShowDateModal] = useState(false);

    const { data: properties } = useGetLandlordPropertyQuery();
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Prepare query parameters
    const monthName = months[selectedMonth].toLowerCase();
    const apartmentIds = selectedApartment === 'all' ? [] : [selectedApartment];

    const { data: propertiesMetrics, refetch } = useGetPropertyReportMetricsQuery({
        month: dateFilterType === 'month' ? monthName : undefined,
        year: selectedYear,
        apartment_ids: apartmentIds.length ? apartmentIds : undefined
    });


    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

    const handleFilter = () => {
        refetch(); // Trigger data refetch with current filter parameters
    };

    const handleDateTypeSelect = (type) => {
        setDateFilterType(type);
        setShowDateModal(true);
    };

    const handleDateConfirm = () => {
        setShowDateModal(false);
        handleFilter(); // Apply filter after date selection
    };

    // Auto-refresh data when filters change
    useEffect(() => {
        if (dateFilterType) { // Only fetch if date filter is set
            handleFilter();
        }
    }, [selectedApartment, selectedMonth, selectedYear, dateFilterType]);

    return (
        <div className="mt-4 p-2">
            {/* Enhanced Filter Section */}
            <div className="flex justify-between items-center mb-8">
                <h2 className="font-bold text-gray-800 text-xl">
                    {selectedApartment === 'all' || selectedApartment === ''
                        ? 'Property Metrics'
                        : `${properties?.results?.find(p => p.id === selectedApartment)?.name || 'Property'} Metrics`}
                    {dateFilterType && (
                        <span className="ml-2 font-normal text-gray-600 text-lg">
                            {dateFilterType === 'month'
                                ? `for ${months[selectedMonth]}, ${selectedYear}`
                                : `for ${selectedYear}`}
                        </span>
                    )}
                </h2>

                <div className="flex items-center space-x-3 bg-white shadow-lg p-2 rounded-lg">
                    {/* Property Select */}
                    <div className="relative">
                        <select
                            className="bg-white py-1.5 pr-8 pl-3 border border-gray-200 focus:border-purple-500 rounded-md focus:ring-2 focus:ring-purple-500 text-sm appearance-none cursor-pointer"
                            value={selectedApartment}
                            onChange={(e) => setSelectedApartment(e.target.value)}
                        >
                            <option value='all'>All Properties</option>
                            {properties?.results?.map(apt => (
                                <option key={apt.id} value={apt.id}>{apt.name}</option>
                            ))}
                        </select>
                        <div className="right-0 absolute inset-y-0 flex items-center px-2 text-gray-500 pointer-events-none">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>

                    {/* Date filter */}
                    <div className="relative flex items-center">

                        <select
                            onChange={(e) => handleDateTypeSelect(e.target.value)}
                            value={dateFilterType}
                            className="bg-white py-1.5 pr-8 pl-3 border border-gray-200 focus:border-purple-500 rounded-md focus:ring-2 focus:ring-purple-500 text-sm appearance-none cursor-pointer"
                        >
                            <option value="" disabled>Select Date Filter</option>
                            <option value="month">Specific Month</option>
                            <option value="year">Specific Year</option>
                        </select>
                        <div className="right-0 absolute inset-y-0 flex items-center px-2 text-gray-500 pointer-events-none">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>


                    </div>


                </div>
            </div>

            {/* Key Metrics Grid */}
            <div className="gap-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mb-8">
                {[
                    {
                        title: "Rent Collection",
                        value: `KES ${selectedApartment === 'all'
                            ? (propertiesMetrics?.average_metrics?.paid_amount || 0)?.toLocaleString()
                            : (propertiesMetrics?.apartment_metrics[0]?.paid_amount || 0)?.toLocaleString()}`,
                        change: selectedApartment === 'all'
                            ? `${propertiesMetrics?.average_metrics?.comparisons?.paid_amount?.change || 0}%`
                            : `${propertiesMetrics?.apartment_metrics[0]?.comparisons?.paid_amount?.change || 0}%`,
                        icon: selectedApartment === 'all'
                            ? propertiesMetrics?.average_metrics?.comparisons?.paid_amount?.direction === "down" ? "↓"
                                : propertiesMetrics?.average_metrics?.comparisons?.paid_amount?.direction === "no_change" ? "" : "↑"
                            : propertiesMetrics?.apartment_metrics[0]?.comparisons?.paid_amount?.direction === "down" ? "↓"
                                : propertiesMetrics?.apartment_metrics[0]?.comparisons?.paid_amount?.direction === "no_change" ? "" : "↑",
                        colorIcon: selectedApartment === 'all'
                            ? propertiesMetrics?.average_metrics?.comparisons?.paid_amount?.direction === "down" ? "red"
                                : propertiesMetrics?.average_metrics?.comparisons?.paid_amount?.direction === "no_change" ? "gray" : "green"
                            : propertiesMetrics?.apartment_metrics[0]?.comparisons?.paid_amount?.direction === "down" ? "red"
                                : propertiesMetrics?.apartment_metrics[0]?.comparisons?.paid_amount?.direction === "no_change" ? "gray" : "green",
                        color: "green",
                        description: "Total collected rent"
                    },
                    {
                        title: "Vacancy Rate",
                        value: `${selectedApartment === 'all'
                            ? propertiesMetrics?.average_metrics?.occupancy_rate?.toFixed(2) || 0
                            : propertiesMetrics?.apartment_metrics[0]?.occupancy_rate?.toFixed(2) || 0}%`,
                        change: selectedApartment === 'all'
                            ? `${propertiesMetrics?.average_metrics?.comparisons?.occupancy_rate?.change || 0}%`
                            : `${propertiesMetrics?.apartment_metrics[0]?.comparisons?.occupancy_rate?.change || 0}%`,
                        icon: selectedApartment === 'all'
                            ? propertiesMetrics?.average_metrics?.comparisons?.occupancy_rate?.direction === "down" ? "↓" : "↑"
                            : propertiesMetrics?.apartment_metrics[0]?.comparisons?.occupancy_rate?.direction === "down" ? "↓" : "↑",
                        colorIcon: selectedApartment === 'all'
                            ? propertiesMetrics?.average_metrics?.comparisons?.occupancy_rate?.direction === "down" ? "red" : "green"
                            : propertiesMetrics?.apartment_metrics[0]?.comparisons?.occupancy_rate?.direction === "down" ? "red" : "green",
                        description: "Percentage of occupied units",
                        color: "blue",
                    },
                    {
                        title: "Maintenance Costs",
                        value: `KES ${selectedApartment === 'all'
                            ? (propertiesMetrics?.average_metrics?.maintenance_cost || 0)?.toLocaleString()
                            : (propertiesMetrics?.apartment_metrics[0]?.maintenance_cost || 0)?.toLocaleString()}`,
                        change: selectedApartment === 'all'
                            ? `${propertiesMetrics?.average_metrics?.comparisons?.maintenance_cost?.change || 0}%`
                            : `${propertiesMetrics?.apartment_metrics[0]?.comparisons?.maintenance_cost?.change || 0}%`,
                        icon: selectedApartment === 'all'
                            ? propertiesMetrics?.average_metrics?.comparisons?.maintenance_cost?.direction === "up"
                                ? "↑"
                                : propertiesMetrics?.average_metrics?.comparisons?.maintenance_cost?.direction === "down"
                                    ? "↓"
                                    : ""
                            : propertiesMetrics?.apartment_metrics[0]?.comparisons?.maintenance_cost?.direction === "up"
                                ? "↑"
                                : propertiesMetrics?.apartment_metrics[0]?.comparisons?.maintenance_cost?.direction === "down"
                                    ? "↓"
                                    : "",
                        colorIcon: selectedApartment === 'all'
                            ? propertiesMetrics?.average_metrics?.comparisons?.maintenance_cost?.direction === "down" ? "red"
                                : propertiesMetrics?.average_metrics?.comparisons?.maintenance_cost?.direction === "no_change" ? "gray" : "green"
                            : propertiesMetrics?.apartment_metrics[0]?.comparisons?.maintenance_cost?.direction === "down" ? "red"
                                : propertiesMetrics?.apartment_metrics[0]?.comparisons?.maintenance_cost?.direction === "no_change" ? "gray" : "green",
                        description: "Total maintenance spend",
                        color: "yellow",

                    },
                    {
                        title: "Property Profit",
                        value: `KES ${selectedApartment === 'all'
                            ? (propertiesMetrics?.average_metrics?.property_profit || 0)?.toLocaleString()
                            : (propertiesMetrics?.apartment_metrics[0]?.property_profit || 0)?.toLocaleString()}`,
                        change: selectedApartment === 'all'
                            ? `${propertiesMetrics?.average_metrics?.comparisons?.property_profit?.change || 0}%`
                            : `${propertiesMetrics?.apartment_metrics[0]?.comparisons?.property_profit?.change || 0}%`,
                        icon: selectedApartment === 'all'
                            ? propertiesMetrics?.average_metrics?.comparisons?.property_profit?.direction === "down" ? "↓"
                                : propertiesMetrics?.average_metrics?.comparisons?.property_profit?.direction === "no_change" ? "" : "↑"
                            : propertiesMetrics?.apartment_metrics[0]?.comparisons?.property_profit?.direction === "down" ? "↓"
                                : propertiesMetrics?.apartment_metrics[0]?.comparisons?.property_profit?.direction === "no_change" ? "" : "↑",
                        colorIcon: selectedApartment === 'all'
                            ? propertiesMetrics?.average_metrics?.comparisons?.property_profit?.direction === "down" ? "red"
                                : propertiesMetrics?.average_metrics?.comparisons?.property_profit?.direction === "no_change" ? "gray" : "green"
                            : propertiesMetrics?.apartment_metrics[0]?.comparisons?.property_profit?.direction === "down" ? "red"
                                : propertiesMetrics?.apartment_metrics[0]?.comparisons?.property_profit?.direction === "no_change" ? "gray" : "green",
                        description: "Net profit from properties",
                        color: "purple"
                    }
                ].map((metric, index) => (
                    <div
                        key={index}
                        className={`bg-white p-6 rounded-xl shadow-lg hover:shadow-md transition-shadow duration-200 border-l-4 border-${metric.color}-500 cursor-pointer ${hoveredMetric === index ? 'ring-2 ring-blue-200' : ''}`}
                        onMouseEnter={() => setHoveredMetric(index)}
                        onMouseLeave={() => setHoveredMetric(null)}
                    >
                        <div className="flex justify-between items-start">
                            <div>
                                <h6 className="mb-2 font-medium text-gray-500">{metric.title}</h6>
                                <p className="mb-1 font-bold text-gray-800 text-2xl">{metric.value}</p>
                            </div>
                            {metric.change && (
                                <span className={`text-xs px-2 py-1 rounded-full bg-${metric.colorIcon}-100 text-${metric.colorIcon}-800`}>
                                    {metric.icon} {metric.change}
                                </span>
                            )}
                        </div>
                        <p className={`text-xs text-${metric.color}-600 mt-1`}>{metric.description}</p>
                    </div>
                ))}
            </div>

            <PropertyChart />

            {/* Date Selection Modal */}
            {showDateModal && (
                <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
                    <div className="bg-white shadow-xl p-6 rounded-lg w-full max-w-sm">
                        <h3 className="mb-4 font-semibold text-lg">
                            Select {dateFilterType === 'month' ? 'Month and Year' : 'Year'}
                        </h3>

                        {dateFilterType === 'month' && (
                            <div className="mb-4">
                                <p className="block mb-1 font-medium text-gray-700 text-sm">Month</p>
                                <select
                                    className="p-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                >
                                    {months.map((month, index) => (
                                        <option key={month} value={index}>{month}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="mb-6">
                            <p className="block mb-1 font-medium text-gray-700 text-sm">Year</p>
                            <select
                                className="p-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            >
                                {years.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                className="hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md text-gray-700"
                                onClick={() => setShowDateModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-white"
                                onClick={handleDateConfirm}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}